import { Controller } from "@hotwired/stimulus"
import * as CookieConsent from "vanilla-cookieconsent"
import { denyRybbit, loadRybbit, unloadRybbit } from "../lib/rybbit"

const FIRST_TOUCH_COOKIE = "first_touch"
const FIRST_TOUCH_MAX_AGE = 60 * 60 * 24 * 365
const UTM_KEYS = [ "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term" ]

// First-touch snapshot pris au premier connect() (page d'atterrissage), PAS au clic Accept.
// Le consentement peut arriver après quelques visites Turbo vers des URLs sans UTM : sans ce
// snapshot on perdrait utm_source/referrer d'origine. En mémoire uniquement (aucune persistance
// avant consentement) — un rechargement complet le réinitialise, comportement acceptable.
let firstTouchSnapshot = null

function hasCookie(name) {
  return document.cookie.split("; ").some((entry) => entry.startsWith(`${name}=`))
}

function currentUtmParams() {
  const params = new URLSearchParams(window.location.search)
  return UTM_KEYS.reduce((utm, key) => {
    if (params.has(key)) utm[key] = params.get(key)
    return utm
  }, {})
}

function captureFirstTouch() {
  if (firstTouchSnapshot) return

  firstTouchSnapshot = {
    ...currentUtmParams(),
    referrer: document.referrer,
    landing_path: window.location.pathname
  }
}

function writeFirstTouchCookie() {
  if (hasCookie(FIRST_TOUCH_COOKIE)) return

  captureFirstTouch()
  document.cookie = `${FIRST_TOUCH_COOKIE}=${encodeURIComponent(JSON.stringify(firstTouchSnapshot))}; path=/; max-age=${FIRST_TOUCH_MAX_AGE}; samesite=lax`
}

// Retrait du consentement (art. 7.3 RGPD) : sans purge, rejeter après avoir accepté
// laisserait le cookie d'attribution un an de plus et le retrait serait cosmétique.
// La librairie n'appelle onReject d'un service que sur la transition accepté ->
// rejeté, jamais au chargement d'une page déjà rejetée.
function eraseFirstTouchCookie() {
  document.cookie = `${FIRST_TOUCH_COOKIE}=; path=/; max-age=0; samesite=lax`
}

// Boots vanilla-cookieconsent (https://cookieconsent.orestbida.com) with a single
// "analytics" category gating first-touch attribution capture. The library persists
// the user's accept/reject decision itself (its own cookie) and won't re-show the
// modal once decided — connect() re-running on every Turbo visit is the library's
// intended usage (it no-ops the UI when consent is already recorded).
export default class extends Controller {
  static values = { config: Object, cookieName: String, locale: String, rybbitSrc: String, silent: Boolean }

  async connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    captureFirstTouch()

    const texts = this.configValue
    const locale = this.localeValue

    await CookieConsent.run({
      // Les consentements donnés avant l'ajout de Rybbit (#2834) puis de ses session
      // replays ne couvrent pas ces finalités : incrémenter la révision pour redemander,
      // jamais pour un simple ajustement.
      revision: 2,
      cookie: { name: this.cookieNameValue },
      // Layouts sans bandeau (onboarding, paywall) : on lit le choix déjà fait, sans UI.
      autoShow: !this.silentValue,
      guiOptions: {
        // "cloud inline" / "bottom center" recouvrait le contenu interactif :
        // le formulaire du calculateur de marge sur desktop, et le texte de
        // preuve sociale tranché net sous les CTA de la homepage (audit SEO
        // 2026-08-27). "box" / "bottom right" est le pattern coin compact
        // recommandé par la lib pour ne jamais chevaucher le contenu
        // principal (cf. docs/advanced/ui-customization.md du projet).
        consentModal: {
          layout: "box",
          position: "bottom right"
        }
      },
      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
          services: {
            firstTouch: {
              onAccept: writeFirstTouchCookie,
              onReject: eraseFirstTouchCookie
            },
            // Rybbit (#2834) : chargé seulement après consentement, car il pose un
            // identifiant visiteur dans le localStorage (hors exemption CNIL). Vide
            // hors production : loadRybbit() ne fait alors rien.
            rybbit: {
              onAccept: () => loadRybbit(this.rybbitSrcValue),
              onReject: unloadRybbit
            }
          }
        }
      },
      language: {
        default: locale,
        translations: {
          [locale]: texts
        }
      }
    })

    // onReject ne couvre que le passage accepté -> refusé dans la même page. Consentement
    // expiré, invalidé par `revision` ou jamais donné : rien n'a déclenché la purge, alors
    // que l'identifiant Rybbit et le cookie d'attribution d'une visite précédente restent.
    if (!CookieConsent.acceptedCategory("analytics")) {
      denyRybbit()
      eraseFirstTouchCookie()
    }
  }
}
